import { useMemo } from "react";
import cx from "classnames";

import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import CollapsibleSection from "ds/components/CollapsibleSection";
import Badge from "ds/components/Badge";
import { useToggle } from "hooks/useToggle";
import { UserGuideChapterEnriched } from "shared/UserGuides/types";
import { UserGuideStartLocation } from "shared/UserGuides/analytics";

import styles from "./styles.module.css";
import UserGuideGroupGuideCard from "./GuideCard";

type UserGuideGroupChapterSectionProps = {
  chapter: UserGuideChapterEnriched;
  groupId: string;
  location: UserGuideStartLocation;
  initiallyCollapsed?: boolean;
  initiallyExpandedGuideId?: string;
  variant?: "default" | "assistantPanel";
};

const UserGuideGroupChapterSection = ({
  chapter,
  groupId,
  location,
  initiallyCollapsed = false,
  initiallyExpandedGuideId,
  variant = "default",
}: UserGuideGroupChapterSectionProps) => {
  const [isCollapsed, handleToggle] = useToggle(initiallyCollapsed);

  const completedCount = useMemo(
    () => chapter.guides.filter((g) => g.isCompleted).length,
    [chapter.guides]
  );

  const totalCount = chapter.guides.length;
  const progressText = `${completedCount}/${totalCount}`;

  const badgeVariant = useMemo(() => {
    if (completedCount === 0) return "gray";
    if (completedCount === totalCount) return "green";
    return "blue";
  }, [completedCount, totalCount]);

  return (
    <CollapsibleSection
      title={
        <Box direction="column" gap="small">
          <Box direction="row" gap="medium" align="center">
            <Typography tag="h3" variant="p-t7">
              {chapter.name}
            </Typography>
            <Badge variant={badgeVariant} type="weak" text={progressText} />
          </Box>
          <Typography color="secondary" tag="p" variant="p-body3">
            {chapter.description}
          </Typography>
        </Box>
      }
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      ariaLevel={3}
      ariaLabel={chapter.name}
      contentGap="medium"
      padding={variant === "assistantPanel" ? undefined : "large"}
      className={cx(styles.chapterSection, {
        [styles.chapterSectionAssistantPanel]: variant === "assistantPanel",
      })}
    >
      <Box direction="column" gap="medium">
        {chapter.guides.map((guide) => (
          <UserGuideGroupGuideCard
            key={guide.id}
            guide={guide}
            groupId={groupId}
            location={location}
            initiallyCollapsed={initiallyExpandedGuideId !== guide.id}
          />
        ))}
      </Box>
    </CollapsibleSection>
  );
};

export default UserGuideGroupChapterSection;
